import { useContext, useState, useEffect } from 'react';
import { Row, Col, Button, Modal, ModalBody, FormGroup } from 'reactstrap';
import {
    TextInput,
    SelectInput,
    stateOptions,
} from '~/shared/components/form/Inputs';
import { useForm } from 'react-hook-form';
import { createchangerequest, postVendorAddress } from '~/services/campaign';
import { useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { CampaignContext } from '~/context/campaign';
import { FundRequestContext } from '~/context/fundrequests';

export type AddAddressModalProps = {
    isOpen: boolean;
    toggle: () => void;
    setPayeeDetails: (details: any) => void;
};

export const AddAddressModal = (props: AddAddressModalProps) => {
    const [{ campaign }, campaignDispatch] = useContext(CampaignContext);
    const [{ selectedPayee }, fundRequestDispatch] = useContext(FundRequestContext);
    const { isOpen, toggle } = props;
    const [loading, setLoading] = useState(false);
    const [userEditedLabel, setUserEditedLabel] = useState(false);
    const { vendorId: urlVendorId } = useParams<{ vendorId: string }>();
    const { watch, control, reset, getValues, setValue, handleSubmit } = useForm();

    // Watch address_1 to set default address_label
    const address1 = watch('address_1');
    const addressLabel = watch('address_label');

    // Update address_label when address_1 changes and the user hasn't manually edited the label
    useEffect(() => {
        if (address1 && !userEditedLabel) {
            setValue('address_label', address1);
        }
    }, [address1, userEditedLabel]);

    // Handle manual changes to address_label
    const handleAddressLabelChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        // If the user types something different from address1, mark as user-edited
        if (e.target.value !== address1) {
            setUserEditedLabel(true);
        }

        // If they delete it completely, allow syncing with address1 again
        if (e.target.value === '') {
            setUserEditedLabel(false);
        }
    };

    const getVendorId = () => {
        return urlVendorId || (selectedPayee?.id ? selectedPayee.id : null);
    };

    const submitNewAddress = async () => {
        const { city, address_label, state, address_1, address_2, zip_code } =
            getValues();

        const formdata = {
            address_label: address_label || address_1, // Use address_1 as fallback if no label
            address_1,
            address_2,
            city,
            state,
            zip_code,
        };

        const campaignId = campaign?.campaignDetail?.campaign_id;
        const vendorId = getVendorId();

        if (!vendorId) {
            toast.error('Vendor ID not found');
            return;
        }

        try {
            setLoading(true);
            const updatedVendor = await postVendorAddress(
                formdata,
                vendorId,
                campaignId
            );
            if (updatedVendor?.error) {
                toast.error('Error creating address');
                return;
            }

            // Find the newly created address in the updated vendor data
            const newAddress = updatedVendor.vendor_addresses?.find(
                (address: any) => address.address_label === formdata.address_label
            );

            // If we have a newly created address, update the fundRequest context with it
            if (newAddress) {
                // Set the newly created address as the selected one in the fundRequest
                fundRequestDispatch({
                    type: 'set_field',
                    payload: {
                        field: 'vendor_address',
                        value: newAddress
                    }
                });

                console.log('Setting newly created address as selected:', newAddress.id);
            }

            // Pass the updated vendor back to the parent component
            props.setPayeeDetails(updatedVendor);
            reset();
            setUserEditedLabel(false);
            toggle();
            toast.success('Address created successfully');
        } catch (error) {
            toast.error('Error creating address');
        } finally {
            setLoading(false);
        }
    };

    const selectedPaymentType = watch('payment_type');

    return (
        <Modal centered size='lg' isOpen={isOpen} toggle={toggle}>
            <ModalBody>
                <h3>Add Address</h3>

                <FormGroup>
                    <TextInput
                        label='Street Address'
                        name='address_1'
                        type='text'
                        control={control}
                        required
                    />
                    <TextInput
                        label='Street Address 2'
                        name='address_2'
                        type='text'
                        control={control}
                    />
                    <Row>
                        <Col sm='6'>
                            <TextInput
                                label='City'
                                name='city'
                                type='text'
                                control={control}
                                required
                            />
                        </Col>
                        <Col sm='3'>
                            <SelectInput
                                control={control}
                                name='state'
                                label='State'
                                options={stateOptions}
                                required
                            />
                        </Col>
                        <Col sm='3'>
                            <TextInput
                                name='zip_code'
                                label='Zip Code'
                                type='zip'
                                control={control}
                                required
                            />
                        </Col>
                    </Row>
                    <TextInput
                        label='Address Label'
                        name='address_label'
                        type='text'
                        helperText="for example: Main House, Grandma's House, etc."
                        control={control}
                        onChange={handleAddressLabelChange}
                    />
                </FormGroup>
                <Button color='outline-dark' onClick={toggle}>
                    Cancel
                </Button>
                <Button
                    color='primary'
                    onClick={handleSubmit(submitNewAddress)}
                    disabled={loading}
                >
                    {loading ? 'Submitting...' : 'Submit'}
                </Button>
            </ModalBody>
        </Modal>
    );
};
